// Bridge mock-state changes into the notification store. Each subscription
// fires only on net-new entries — initial fixture load shouldn't drown the
// dashboard in toasts.

import { useAgentStore } from '../stores/agent';
import { useConversationsStore } from '../stores/conversations';
import { useNotificationsStore } from '../stores/notifications';
import { useVisitorsStore } from '../stores/visitors';
import { visitorDisplayName } from './visitor-name';

/** Notifications only fire while an agent is logged in. */
function isLoggedIn(): boolean {
  return useAgentStore.getState().current !== null;
}

let installed = false;

// Toast suppression — hydration of historical visitors + conversations from
// the server adds many rows at once. None of those are "new activity" from
// the agent's perspective, so we mute toasts while hydration runs.
let suppressedDepth = 0;
// Time-based quiet window armed on each bus (re)connection. Synchronous
// hydration is covered by withSuppressedToasts, but live visitors re-announce
// via presence heartbeats over the ~10s AFTER connecting — and on a re-login
// the store was just cleared, so every online visitor looks brand-new. Without
// this, switching agents (logout → login) produces a toast per online visitor.
let quietUntil = 0;
function isSuppressed(): boolean {
  return suppressedDepth > 0 || Date.now() < quietUntil;
}

/** Mute visitor/conversation toasts for `ms` after a (re)connection so the
 *  presence-heartbeat replay of already-online visitors doesn't toast. Slightly
 *  longer than the 10s widget heartbeat so one full cycle is covered. */
export function armToastQuietWindow(ms = 12_000): void {
  quietUntil = Date.now() + ms;
}

/**
 * Wrap a store-mutating block to suppress visitor/conversation toasts for
 * its duration. Re-entrant — multiple suppressions stack and each release
 * decrements the depth. After the block ends, the bridge's prev*Ids sets
 * are updated to include everything we added, so the very-next live event
 * is still flagged as new.
 */
export async function withSuppressedToasts(fn: () => void | Promise<void>): Promise<void> {
  suppressedDepth += 1;
  try {
    await fn();
  } finally {
    // Drop the depth on the NEXT microtask so the store's subscribe()
    // callbacks (which run synchronously after set() in zustand) all see
    // the still-suppressed state.
    await Promise.resolve();
    suppressedDepth = Math.max(0, suppressedDepth - 1);
  }
}

export function installEventBridge(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // ─── Visitors: toast when a row appears that wasn't present a moment ago ───
  let prevVisitorIds = new Set<string>(
    Array.from(useVisitorsStore.getState().rows.keys()) as unknown as string[],
  );
  useVisitorsStore.subscribe((state) => {
    const nowIds = new Set<string>(Array.from(state.rows.keys()) as unknown as string[]);
    const incoming = [...nowIds].filter((id) => !prevVisitorIds.has(id));
    const departed = [...prevVisitorIds].filter((id) => !nowIds.has(id));
    prevVisitorIds = nowIds;

    if (!isLoggedIn()) return; // no toasts on the login screen
    if (isSuppressed()) return; // hydration in progress

    for (const id of incoming) {
      const row = state.rows.get(id as never);
      if (!row) continue;
      const where = originHost(row.context.origin);
      useNotificationsStore.getState().push({
        kind: 'visitor',
        title: visitorDisplayName(row.identity.visitorId, row.identity.name),
        body: `${where}${row.context.pagePath}`,
      });
    }
    for (const id of departed) {
      useNotificationsStore.getState().push({
        kind: 'disconnect',
        title: `${visitorDisplayName(id)} left`,
        ttl: 3500,
      });
    }
  });

  // ─── Conversations: toast on visitor-initiated chat ───
  let prevConvIds = new Set<string>(
    Array.from(useConversationsStore.getState().byId.keys()) as unknown as string[],
  );
  useConversationsStore.subscribe((state) => {
    const nowIds = new Set<string>(Array.from(state.byId.keys()) as unknown as string[]);
    const incoming = [...nowIds].filter((id) => !prevConvIds.has(id));
    prevConvIds = nowIds;

    if (!isLoggedIn()) return;
    if (isSuppressed()) return; // hydration in progress

    for (const id of incoming) {
      const conv = state.byId.get(id as never);
      if (!conv) continue;
      if (conv.startedBy === 'visitor') {
        useNotificationsStore.getState().push({
          kind: 'chat',
          title: `${conv.visitorName} wants to chat`,
          body: conv.medium === 'video' ? 'video call requested' : 'chat request',
          sessionId: conv.sessionId,
        });
      }
    }
  });
}

function originHost(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return origin;
  }
}
